/**
 * DataSpec Processor
 *
 * Processes dataSpec objects from API responses to create complete visualizations
 * from CSV data. This solves the problem of incomplete AI-generated visualizations
 * by filtering and aggregating data client-side.
 *
 * @see docs/DATASPEC_FRONTEND_GUIDE.md for full documentation
 */

/**
 * Map raw CSV field names to normalized property names
 * This matches the column mapping used in dataLoader.js
 * Used to translate API dataSpec field names to frontend property names
 */
const FIELD_MAPPING = {
  // Core fields (both health and education)
  facility_name: 'name',
  institution_name: 'name',
  school_name: 'school_name',
  facility_id: 'id',
  facility_level: 'level',

  // Location fields
  lat: 'latitude',
  lng: 'longitude',
  long: 'longitude',

  // Health: Infrastructure
  has_backup_power: 'functional_backup_power',
  has_laboratory: 'laboratory',
  has_treatment_room: 'treatment_room',
  has_consulting_room: 'consulting_room',
  has_staff_accommodation: 'staff_accommodation',
  has_reception: 'reception',
  has_waste_disposal: 'waste_disposal_incinerator',
  has_gate: 'gate',
  has_delivery_room: 'delivery_room',
  has_inpatient_ward: 'inpatient_ward',

  // Health: Services
  has_immunization: 'immunization_services',
  has_maternal_health: 'maternal_and_child_health_services',
  has_hiv_tb_care: 'hivaids_and_tb_care',
  has_diagnostics: 'basic_diagnostic_services',
  has_counselling: 'counselling',
  has_health_education: 'health_education',
  has_family_planning: 'family_planning_services',
  has_surgery: 'advanced_surgical_procedures',
  has_outpatient: 'outpatient_services',

  // Education: Infrastructure/Facilities
  has_electricity: 'electricity_available',
  has_water: 'water_available',
  has_handwashing: 'handwashing_available',
  has_ict_lab: 'ict_lab_available',
  has_teacher_toilets: 'teacher_toilets_available',
  has_disability_accessible_toilets: 'disability_accessible_toilets',
  has_separate_gender_toilets: 'separate_gender_toilets',

  // Education: Staff
  staff_lab_assistant: 'laboratory_assistants',
  staff_lab_technician: 'laboratory_technicians',
  laboratory_technician: 'laboratory_technicians',
  staff_medical_officers: 'medical_officers',
  staff_clinical_officer: 'clinical_officers',
  staff_midwives: 'enrolled_midwives',
  staff_nursing_officers: 'nursing_officers',
  staff_vht_chw: 'vhts_chws',
  staff_comprehensive_nurse: 'comprehensive_enrolled_nurse',
  staff_enrolled_nurses: 'enrolled_nurses',
  staff_health_info: 'health_information_assistants',

  // Education: Supplies/Equipment
  has_respiratory_timers: 'digital_respiratory_timers',
  has_ors_sachets: 'oral_rehydration_salts',
  oral_rehydration_salts_ors_sachets_for_management_of_diarrhoea:
    'oral_rehydration_salts',
  has_zinc_tablets: 'zinc_tablets',
  zinc_tablets_for_treating_diarrhoea: 'zinc_tablets',
  has_rdt_kits: 'rapid_diagnostic_test_kits_for_malaria',
  rapid_diagnostic_test_rdt_kits_for_malaria:
    'rapid_diagnostic_test_kits_for_malaria',
  has_act_tablets: 'act_tablets_for_treatment_of_malaria',
  has_amoxicillin: 'amoxicillin_tablets_for_treatment_of_pneumonia',
  has_thermometers: 'digital_clinical_thermometers_for_measuring_fever',

  // Water & Sanitation
  has_female_toilet: 'toilet_for_females',
  has_accessible_toilet: 'toilet_for_people_with_reduced_mobility',
}

/**
 * Apply dataSpec filters to CSV data
 * @param {Array} data - Parsed CSV data (already normalized by dataLoader)
 * @param {Array} filters - Array of filter conditions (using raw CSV field names)
 * @returns {Array} Filtered data
 */
export function applyFilters(data, filters) {
  if (!data || !Array.isArray(data)) {
    console.warn('applyFilters: Invalid data provided')
    return []
  }

  if (!filters || filters.length === 0) {
    return data
  }

  return data.filter(row => {
    return filters.every(filter => {
      // Map the filter field name to the normalized property name
      const normalizedField = FIELD_MAPPING[filter.field] || filter.field
      const fieldValue = row[normalizedField]

      switch (filter.operator) {
        case 'equals':
          return (
            String(fieldValue).toLowerCase() ===
            String(filter.value).toLowerCase()
          )

        case 'notEquals':
          return (
            String(fieldValue).toLowerCase() !==
            String(filter.value).toLowerCase()
          )

        case 'contains':
          return String(fieldValue)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase())

        case 'startsWith':
          return String(fieldValue)
            .toLowerCase()
            .startsWith(String(filter.value).toLowerCase())

        case 'gt':
          return parseFloat(fieldValue) > parseFloat(filter.value)

        case 'gte':
          return parseFloat(fieldValue) >= parseFloat(filter.value)

        case 'lt':
          return parseFloat(fieldValue) < parseFloat(filter.value)

        case 'lte':
          return parseFloat(fieldValue) <= parseFloat(filter.value)

        case 'exists':
          return (
            fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
          )

        default:
          console.warn(`Unknown operator: ${filter.operator}`)
          return true
      }
    })
  })
}

/**
 * Aggregate data by grouping field
 * @param {Array} data - Filtered CSV data
 * @param {Object} spec - DataSpec aggregation config
 * @returns {Array} Aggregated results
 */
export function aggregateData(data, spec) {
  if (!data || !Array.isArray(data)) {
    console.warn('aggregateData: Invalid data provided')
    return []
  }

  const { groupBy, aggregation, metric, sortBy, limit } = spec

  if (!groupBy) {
    console.warn('aggregateData: groupBy field is required')
    return []
  }

  // Group data
  const groups = {}
  data.forEach(row => {
    const key = row[groupBy] || 'Unknown'
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(row)
  })

  // Calculate aggregation
  let results = Object.entries(groups).map(([key, rows]) => {
    let value

    switch (aggregation) {
      case 'count': {
        value = rows.length
        break
      }
      case 'sum': {
        value = rows.reduce((sum, row) => {
          const val = parseFloat(row[metric])
          return sum + (isNaN(val) ? 0 : val)
        }, 0)
        break
      }
      case 'avg': {
        const sum = rows.reduce((s, row) => {
          const val = parseFloat(row[metric])
          return s + (isNaN(val) ? 0 : val)
        }, 0)
        value = rows.length > 0 ? sum / rows.length : 0
        break
      }
      case 'min': {
        const validValues = rows
          .map(row => parseFloat(row[metric]))
          .filter(val => !isNaN(val))
        value = validValues.length > 0 ? Math.min(...validValues) : 0
        break
      }
      case 'max': {
        const maxValues = rows
          .map(row => parseFloat(row[metric]))
          .filter(val => !isNaN(val))
        value = maxValues.length > 0 ? Math.max(...maxValues) : 0
        break
      }
      default:
        value = rows.length
    }

    return {
      location: key,
      value: value,
      count: rows.length,
      metric: metric || 'count',
    }
  })

  // Sort
  if (sortBy) {
    results.sort((a, b) =>
      sortBy === 'desc' ? b.value - a.value : a.value - b.value
    )
  }

  // Limit
  if (limit && limit > 0) {
    results = results.slice(0, limit)
  }

  return results
}

/**
 * Select only specified fields from data
 * @param {Array} data - Data array
 * @param {Array} fields - Field names to select (raw CSV names)
 * @returns {Array} Data with only specified fields (using normalized names)
 */
export function selectFields(data, fields) {
  if (!fields || fields.length === 0) {
    return data
  }

  return data.map(row => {
    const selected = {}
    fields.forEach(field => {
      // Map field name and use normalized field
      const normalizedField = FIELD_MAPPING[field] || field
      selected[normalizedField] = row[normalizedField]
    })
    return selected
  })
}

/**
 * Process dataSpec and return ready-to-use data
 * @param {Object} dataSpec - DataSpec from API response
 * @param {Array} csvData - Loaded CSV data
 * @returns {Object|null} Processed data for visualizations
 */
export function processDataSpec(dataSpec, csvData) {
  if (!dataSpec || !csvData) {
    return null
  }

  try {
    // Apply filters
    let filtered = applyFilters(csvData, dataSpec.filters)

    // Handle empty results
    if (filtered.length === 0) {
      console.info('processDataSpec: No data matched the filters')
      return {
        type: dataSpec.type,
        facilities: [],
        aggregated: [],
        raw: [],
      }
    }

    // Aggregate if needed
    if (dataSpec.type === 'aggregated' || dataSpec.type === 'comparison') {
      const aggregated = aggregateData(filtered, dataSpec)

      // Select fields if specified
      const selectedFiltered =
        dataSpec.fields?.length > 0
          ? selectFields(filtered, dataSpec.fields)
          : filtered

      return {
        type: dataSpec.type,
        aggregated: aggregated,
        raw: selectedFiltered,
      }
    }

    // Return facilities (with field selection)
    const facilities =
      dataSpec.fields?.length > 0
        ? selectFields(filtered, dataSpec.fields)
        : filtered

    return {
      type: 'facilities',
      facilities: facilities,
    }
  } catch (error) {
    console.error('Error processing dataSpec:', error)
    return null
  }
}

/**
 * Create Leaflet markers from facilities data
 * @param {Array} facilities - Facility data with lat/lng
 * @param {Object} options - Marker creation options
 * @returns {Array} Leaflet marker configurations
 */
export function createMarkersFromFacilities(facilities, options = {}) {
  if (!facilities || !Array.isArray(facilities)) {
    return []
  }

  const {
    nameField = 'facility_name',
    latField = 'latitude',
    lngField = 'longitude',
    popupFields = [],
    popupTemplate = null,
  } = options

  return facilities
    .filter(f => {
      const lat = parseFloat(f[latField])
      const lng = parseFloat(f[lngField])
      return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0
    })
    .map(facility => {
      const lat = parseFloat(facility[latField])
      const lng = parseFloat(facility[lngField])
      const name = facility[nameField] || 'Unknown'

      // Create popup content
      let popupHtml
      if (popupTemplate) {
        popupHtml = popupTemplate(facility)
      } else if (popupFields.length > 0) {
        popupHtml =
          `<b>${name}</b><br>` +
          popupFields
            .map(field => `${field}: ${facility[field] || 'N/A'}`)
            .join('<br>')
      } else {
        popupHtml = `<b>${name}</b>`
      }

      return {
        position: [lat, lng],
        popup: popupHtml,
      }
    })
}

/**
 * Create ECharts bar chart config from aggregated data
 * @param {Array} aggregated - Aggregated data from processDataSpec
 * @param {Object} options - Chart configuration options
 * @returns {Object} ECharts configuration
 */
export function createBarChartFromAggregated(aggregated, options = {}) {
  if (!aggregated || !Array.isArray(aggregated) || aggregated.length === 0) {
    return null
  }

  const {
    title = 'Data Comparison',
    xAxisLabel = 'Location',
    yAxisLabel = 'Value',
    seriesName = 'Count',
  } = options

  return {
    title: {
      text: title,
      left: 'center',
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    xAxis: {
      type: 'category',
      data: aggregated.map(item => item.location),
      name: xAxisLabel,
      axisLabel: {
        rotate: aggregated.length > 3 ? 45 : 0,
        interval: 0,
      },
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
    },
    series: [
      {
        name: seriesName,
        type: 'bar',
        data: aggregated.map(item => item.value),
        label: {
          show: aggregated.length <= 20,
          position: 'top',
        },
      },
    ],
  }
}

/**
 * Check if AI visualization is complete compared to expected data
 * @param {Object} visualization - AI-generated visualization
 * @param {Object} dataCompleteness - Data completeness metadata from API
 * @param {Object} response - Full API response
 * @returns {Object} Completeness assessment
 */
export function checkVisualizationCompleteness(
  visualization,
  dataCompleteness,
  response
) {
  if (!visualization || !dataCompleteness) {
    return { isComplete: false, reason: 'Missing data' }
  }

  if (visualization.type === 'leaflet') {
    // Check map markers
    const aiMarkerCount = visualization.config?.markers?.length || 0
    const expectedCount = dataCompleteness.facilitiesWithCoordinates

    const isComplete = aiMarkerCount >= expectedCount * 0.9 // 90% threshold

    return {
      isComplete,
      type: 'map',
      aiCount: aiMarkerCount,
      expectedCount: expectedCount,
      percentage:
        expectedCount > 0
          ? ((aiMarkerCount / expectedCount) * 100).toFixed(1)
          : 0,
      reason: isComplete
        ? 'Map is complete'
        : `Map shows only ${aiMarkerCount} of ${expectedCount} facilities`,
    }
  }

  if (visualization.type === 'echarts') {
    // Check chart data points
    if (response.type === 'comparison' && response.rankings) {
      const aiDataPoints =
        visualization.config?.xAxis?.data?.length ||
        visualization.config?.series?.[0]?.data?.length ||
        0
      const expectedCount = response.rankings.length

      const isComplete = aiDataPoints >= expectedCount * 0.9

      return {
        isComplete,
        type: 'chart',
        aiCount: aiDataPoints,
        expectedCount: expectedCount,
        percentage:
          expectedCount > 0
            ? ((aiDataPoints / expectedCount) * 100).toFixed(1)
            : 0,
        reason: isComplete
          ? 'Chart is complete'
          : `Chart shows only ${aiDataPoints} of ${expectedCount} categories`,
      }
    }

    // For other chart types, assume complete if it has data
    return {
      isComplete: true,
      type: 'chart',
      reason: 'Chart appears complete',
    }
  }

  return { isComplete: true, reason: 'Unknown visualization type' }
}
